import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { T, useTranslation } from "@redotech/redo-i18n";
import {
  APPROXIMATE_APPROVAL_RATE,
  DEFAULT_REDO_KEEP_PROPORTION,
} from "@redotech/redo-package-protection-common/shipping-claims/shipping-claim";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { ReloadTeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import * as styles from "./auto-claims-upsell-modal.module.css";

function formatWholeDollars(value: number, language: string): string {
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(value));
}

function formatPercent(value: number, language: string): string {
  return new Intl.NumberFormat(language, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCount(value: number, language: string): string {
  return new Intl.NumberFormat(language).format(value);
}

export const AutoClaimsUpsellModal = memo(function AutoClaimsUpsellModal({
  open,
  onClose,
  onAccept,
}: {
  open: boolean;
  onClose: () => void;
  onAccept?: () => void;
}) {
  const { i18n, t } = useTranslation();
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const reloadTeam = useContext(ReloadTeamContext);
  const [loading, setLoading] = useState(false);

  const statsLoad = useLoad(
    async (signal) => {
      if (!open) {
        return undefined;
      }
      return await client.getProactiveClaimsStats({}, { signal });
    },
    [open],
  );

  const handleAccept = useHandler(async () => {
    setLoading(true);
    try {
      await client.acceptAutoClaimsFiling({});
      reloadTeam?.();
      onAccept?.();
      onClose();
    } finally {
      setLoading(false);
    }
  });

  if (!open) {
    return null;
  }

  const stats = statsLoad?.value;
  const hasCompellingStats =
    stats !== undefined &&
    stats.proactiveClaimCount >= 2 &&
    stats.estimatedMerchantSavings >= 50;

  return (
    <RedoBaseModal
      isOpen={open}
      onModalCloseRequested={onClose}
      otherClasses={[styles.modal]}
    >
      <Flex dir="column" gap="none" w="full">
        <Flex dir="column" gap="md" p="3xl">
          {hasCompellingStats ? (
            <>
              <Text fontSize="lg" fontWeight="semibold" textColor="primary">
                <T
                  components={{
                    amount: (
                      <Text as="span" fontWeight="semibold" textColor="success">
                        {null}
                      </Text>
                    ),
                  }}
                  i18nKey="You left <amount>{{amount}}</amount> on the table in the last 30 days"
                  values={{
                    amount: formatWholeDollars(
                      stats.estimatedMerchantSavings,
                      i18n.language,
                    ),
                  }}
                />
              </Text>
              <Text fontSize="sm" textColor="secondary">
                {t(
                  "Never file a carrier claim again, let Redo fight your claims for you",
                )}
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="lg" fontWeight="semibold" textColor="primary">
                {t("You're leaving money on the table")}
              </Text>
              <Text fontSize="sm" textColor="secondary">
                {t(
                  "Never file a carrier claim again, let Redo fight your claims for you",
                )}
              </Text>
            </>
          )}
        </Flex>
        <Flex
          bgColor="tertiary"
          borderColor="secondary"
          borderWidth="1px"
          dir="column"
          gap="3xl"
          p="3xl"
          w="full"
        >
          {hasCompellingStats && (
            <Flex dir="row" justify="space-between" w="full">
              <Flex dir="column" gap="none" pr="lg">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  {t("Claims missed")}
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {formatCount(stats.proactiveClaimCount, i18n.language)}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {t("In the last 30 days")}
                </Text>
              </Flex>
              <Flex dir="column" gap="none" pr="lg">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  {t("Est. recovery")}
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {formatWholeDollars(
                    stats.estimatedMerchantSavings,
                    i18n.language,
                  )}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {t("Still uncollected")}
                </Text>
              </Flex>
              <Flex dir="column" gap="none">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  {t("Win rate")}
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {formatPercent(APPROXIMATE_APPROVAL_RATE, i18n.language)}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {t("Redo average")}
                </Text>
              </Flex>
            </Flex>
          )}
          <Flex dir="column" gap="xs">
            <Flex dir="row" gap="md">
              <Text fontSize="sm" fontWeight="semibold">
                •
              </Text>
              <Text fontSize="sm">
                <T
                  components={{
                    bold: (
                      <Text as="span" fontWeight="semibold">
                        {null}
                      </Text>
                    ),
                  }}
                  i18nKey="Redo files claims automatically for lost or late shipments, <bold>no work on your end.</bold>"
                />
              </Text>
            </Flex>
            <Flex dir="row" gap="md">
              <Text fontSize="sm" fontWeight="semibold">
                •
              </Text>
              <Text fontSize="sm">
                <T
                  components={{
                    bold: (
                      <Text as="span" fontWeight="semibold">
                        {null}
                      </Text>
                    ),
                  }}
                  i18nKey="<bold>Free to turn on</bold>, we only earn when you do."
                />
              </Text>
            </Flex>
            <Flex dir="row" gap="md">
              <Text fontSize="sm" fontWeight="semibold">
                •
              </Text>
              <Text fontSize="sm">
                <T
                  components={{
                    bold: (
                      <Text as="span" fontWeight="semibold">
                        {null}
                      </Text>
                    ),
                  }}
                  i18nKey="<bold>You keep {{percent}}</bold> of every approved claim."
                  values={{
                    percent: formatPercent(
                      1 - DEFAULT_REDO_KEEP_PROPORTION,
                      i18n.language,
                    ),
                  }}
                />
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex dir="row" gap="md" justify="flex-end" p="3xl">
          <RedoButton
            disabled={loading}
            hierarchy="secondary"
            onClick={onClose}
            text={t("No thanks")}
          />
          <RedoButton
            hierarchy="primary"
            onClick={handleAccept}
            pending={loading}
            text={t("Start recovering claims")}
            theme="success"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
